import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  FaSearch, 
  FaThLarge, 
  FaTable,
  FaChartLine,
  FaFire,
  FaUsers,
  FaLayerGroup,
  FaArrowUp,
  FaArrowDown,
  FaCheckCircle
} from 'react-icons/fa';
import '../styles/CollectionsPage.css';

const CollectionsPage = () => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('volumeTotal');
  const [sortDirection, setSortDirection] = useState('desc');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'table'
  const [stats, setStats] = useState({
    totalCollections: 0,
    totalVolume: 0,
    totalItems: 0,
    totalOwners: 0
  });

  useEffect(() => {
    const fetchCollectionData = async () => {
      try {
        let allAssets = [];
        const totalPages = 5;

        for (let page = 1; page <= totalPages; page++) {
          const assetsResponse = await axios.get(`https://api.mainnet.klever.org/v1.0/assets/list?page=${page}&limit=100`);
          const assetsData = assetsResponse.data.data.assets.filter(asset => asset.assetType === "NonFungible");
          allAssets = [...allAssets, ...assetsData];
        }

        const marketplaceIds = [
          'd4f2bab340c55fde', '7c353c02770da029', '2936933ee43db509',
          '417b70c0eb7a33cb', '116056b257d9f6d5', 'aa68ad853a09988c', 'd2a04fe890a6edda'
        ];
        const statsPromises = marketplaceIds.map(id => axios.get(`https://api.mainnet.klever.org/v1.0/marketplaces/${id}/stats`));
        const statsResponses = await Promise.all(statsPromises);
        let combinedStatsData = [];
        statsResponses.forEach(response => combinedStatsData = [...combinedStatsData, ...response.data.data.stats]);

        let totalVolume = 0;
        let totalItems = 0;
        let totalOwners = 0;

        let mergedCollections = allAssets.map(asset => {
          let collectionVolume = 0, collectionItems = 0, collectionOwners = 0;
          let floorPrices = [];
          let royalties = null;

          combinedStatsData.forEach(stat => {
            if (stat.assetId === asset.assetId) {
              collectionVolume += stat.volumeTotal || 0;
              collectionItems += stat.items || 0;
              collectionOwners += stat.owners || 0;
              if (stat.floor && stat.floor > 0) floorPrices.push(stat.floor);
            }
          });

          totalVolume += collectionVolume;
          totalItems += collectionItems;
          totalOwners += collectionOwners;

          const combinedFloorPrice = floorPrices.length > 0 ? Math.min(...floorPrices) : 0;

          if (asset.royalties && asset.royalties.marketPercentage) {
            royalties = (asset.royalties.marketPercentage / 100).toString().replace(/\.00$/, '');
          }

          const metadataObject = asset.uris ? asset.uris.find(uri => uri.key === 'metadata') : null;
          const metadataUri = metadataObject ? metadataObject.value : null;

          return {
            ...asset,
            volumeTotal: collectionVolume,
            items: collectionItems,
            owners: collectionOwners,
            floor: combinedFloorPrice,
            royalties,
            circulatingSupply: asset.circulatingSupply || 0,
            metadataUri
          };
        });

        mergedCollections.sort((a, b) => b.volumeTotal - a.volumeTotal);
        setCollections(mergedCollections);
        
        setStats({
          totalCollections: allAssets.length,
          totalVolume,
          totalItems,
          totalOwners
        });
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCollectionData();
  }, []);

  const handleImageError = (event) => {
    const src = event.target.src;
    if (src.startsWith("https://klever-mint.mypinata.cloud/ipfs/") && src !== "https://klever-mint.mypinata.cloud/ipfs/QmNwWcvPmfNzPa4JQppj8mXcaeM8BA2yA5VAjVWYLzTT7a") {
      event.target.src = src.replace("https://klever-mint.mypinata.cloud/ipfs/", "https://ipfs.io/ipfs/");
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const filteredAndSortedCollections = useMemo(() => {
    const filtered = collections.filter(collection =>
      collection.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      collection.assetId.toLowerCase().includes(searchTerm.toLowerCase())
    );
    return filtered.sort((a, b) => {
      let aVal = a[sortField] || 0;
      let bVal = b[sortField] || 0;
      if (sortField === 'name') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
        return sortDirection === 'asc' ? (aVal < bVal ? -1 : aVal > bVal ? 1 : 0) : (aVal < bVal ? 1 : aVal > bVal ? -1 : 0);
      }
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }, [collections, searchTerm, sortField, sortDirection]);

  const getSortIcon = (field) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <FaArrowUp className="sort-icon" /> : <FaArrowDown className="sort-icon" />;
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(2)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toLocaleString();
  };

  if (loading) {
    return (
      <div className="collections-page">
        <div className="collections-loading">
          <div className="loading-spinner-large"></div>
          <p className="loading-text">Loading collections...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="collections-page">
      {/* Hero Section */}
      <div className="collections-hero">
        <div className="hero-content">
          <h1 className="hero-title">
            <FaLayerGroup className="hero-icon" />
            NFT Collections
          </h1>
          <p className="hero-subtitle">
            Explore and discover the best NFT collections on KleverChain
          </p>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="stats-container">
        <div className="stat-card">
          <div className="stat-icon-wrapper">
            <FaLayerGroup className="stat-icon collections" />
          </div>
          <div className="stat-content">
            <span className="stat-label">Total Collections</span>
            <span className="stat-value">{stats.totalCollections}</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon-wrapper">
            <FaChartLine className="stat-icon volume" />
          </div>
          <div className="stat-content">
            <span className="stat-label">Total Volume</span>
            <span className="stat-value">{formatNumber(stats.totalVolume)} KLV</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon-wrapper">
            <FaFire className="stat-icon items" />
          </div>
          <div className="stat-content">
            <span className="stat-label">Total Items</span>
            <span className="stat-value">{formatNumber(stats.totalItems)}</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon-wrapper">
            <FaUsers className="stat-icon owners" />
          </div>
          <div className="stat-content">
            <span className="stat-label">Total Owners</span>
            <span className="stat-value">{formatNumber(stats.totalOwners)}</span>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="collections-controls">
        <div className="search-container">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search collections by name or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="view-mode-selector">
          <button
            className={`view-mode-button ${viewMode === 'grid' ? 'active' : ''}`}
            onClick={() => setViewMode('grid')}
          >
            <FaThLarge /> Grid
          </button>
          <button
            className={`view-mode-button ${viewMode === 'table' ? 'active' : ''}`}
            onClick={() => setViewMode('table')}
          >
            <FaTable /> Table
          </button>
        </div>
      </div>

      {/* Grid View */}
      {viewMode === 'grid' && (
        <div className="collections-grid">
          {filteredAndSortedCollections.map((collection, index) => (
            <Link
              key={collection.assetId}
              to={{
                pathname: `/collections/${collection.assetId}`,
                state: { metadataUri: collection.metadataUri }
              }}
              className="collection-card"
              style={{ animationDelay: `${Math.min(index * 0.03, 1)}s` }}
            >
              <div className="card-rank">#{index + 1}</div>
              
              <div className="card-image-wrapper">
                {collection.logo ? (
                  <img
                    src={collection.logo}
                    alt={collection.name}
                    className="card-image"
                    onError={handleImageError}
                  />
                ) : (
                  <div className="card-image-placeholder">
                    <FaLayerGroup size={40} />
                  </div>
                )}
              </div>

              <div className="card-content">
                <h3 className="card-title">{collection.name}</h3>
                <span className="card-ticker">{collection.assetId}</span>

                <div className="card-stats">
                  <div className="card-stat">
                    <span className="card-stat-label">Volume</span>
                    <span className="card-stat-value">
                      {formatNumber(collection.volumeTotal)} KLV
                    </span>
                  </div>
                  <div className="card-stat">
                    <span className="card-stat-label">Floor</span>
                    <span className="card-stat-value">
                      {collection.floor > 0 ? `${formatNumber(collection.floor)} KLV` : 'N/A'}
                    </span>
                  </div>
                </div>

                <div className="card-footer">
                  <div className="footer-item">
                    <FaFire className="footer-icon" />
                    <span>{formatNumber(collection.circulatingSupply)} items</span>
                  </div>
                  {collection.owners > 0 && (
                    <div className="footer-item">
                      <FaUsers className="footer-icon" />
                      <span>{formatNumber(collection.owners)} owners</span>
                    </div>
                  )}
                </div>

                {collection.royalties && (
                  <div className="card-badge">
                    <FaCheckCircle /> {collection.royalties}% royalty
                  </div>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Table View */}
      {viewMode === 'table' && (
        <div className="table-container">
          <table className="collections-table">
            <thead>
              <tr>
                <th className="th-rank">#</th>
                <th className="th-collection" onClick={() => handleSort('name')}>
                  <div className="th-content">
                    Collection {getSortIcon('name')}
                  </div>
                </th>
                <th className="th-sortable" onClick={() => handleSort('volumeTotal')}>
                  <div className="th-content">
                    Volume {getSortIcon('volumeTotal')}
                  </div>
                </th>
                <th className="th-sortable desktop-only" onClick={() => handleSort('floor')}>
                  <div className="th-content">
                    Floor {getSortIcon('floor')}
                  </div>
                </th>
                <th className="th-sortable desktop-only" onClick={() => handleSort('royalties')}>
                  <div className="th-content">
                    Royalties {getSortIcon('royalties')}
                  </div>
                </th>
                <th className="th-sortable" onClick={() => handleSort('owners')}>
                  <div className="th-content">
                    Owners {getSortIcon('owners')}
                  </div>
                </th>
                <th className="th-sortable" onClick={() => handleSort('circulatingSupply')}>
                  <div className="th-content">
                    Supply {getSortIcon('circulatingSupply')}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedCollections.map((collection, index) => (
                <tr 
                  key={collection.assetId}
                  style={{ animationDelay: `${Math.min(index * 0.02, 0.6)}s` }}
                >
                  <td className="td-rank">{index + 1}</td>
                  <td className="td-collection">
                    <Link
                      to={{
                        pathname: `/collections/${collection.assetId}`,
                        state: { metadataUri: collection.metadataUri }
                      }}
                      className="collection-link"
                    >
                      <div className="collection-cell">
                        {collection.logo ? (
                          <img
                            src={collection.logo}
                            alt={collection.name}
                            className="collection-logo"
                            onError={handleImageError}
                          />
                        ) : (
                          <div className="collection-logo-placeholder">
                            <FaLayerGroup />
                          </div>
                        )}
                        <div className="collection-info">
                          <span className="collection-name">{collection.name}</span>
                          <span className="collection-ticker">{collection.assetId}</span>
                        </div>
                      </div>
                    </Link>
                  </td>
                  <td className="td-volume">
                    {collection.volumeTotal ? `${formatNumber(collection.volumeTotal)} KLV` : '0 KLV'}
                  </td>
                  <td className="td-floor desktop-only">
                    {collection.floor ? `${formatNumber(collection.floor)} KLV` : 'N/A'}
                  </td>
                  <td className="td-royalties desktop-only">
                    {collection.royalties !== null ? `${collection.royalties}%` : '0%'}
                  </td>
                  <td className="td-owners">
                    {collection.owners ? formatNumber(collection.owners) : '0'}
                  </td>
                  <td className="td-supply">
                    {formatNumber(collection.circulatingSupply)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Empty State */}
      {filteredAndSortedCollections.length === 0 && (
        <div className="empty-state">
          <FaSearch size={60} />
          <h2>No collections found</h2>
          <p>Try adjusting your search terms</p>
        </div>
      )}
    </div>
  );
};

export default CollectionsPage;